import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone as PhoneIcon, Clock, CheckCircle, XCircle, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { adminGet, adminPut, reportError, formatDate, fadeUp } from './adminApi';

const REVIEW_STATUS_META = {
  approuve: { label: 'Approuvé', cls: 'bg-green-100 text-green-700' },
  rejete: { label: 'Rejeté', cls: 'bg-red-100 text-red-700' },
  en_attente: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-700' },
};
const reviewStatusMeta = (status) => REVIEW_STATUS_META[status] || REVIEW_STATUS_META.en_attente;

export function AdminReviewsTab({ adminKey, onCount }) {
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState('');

  const fetchReviews = useCallback(async () => {
    setError('');
    try {
      const res = await adminGet('/api/admin/reviews', adminKey);
      setReviews(res.data.reviews || []);
      if (onCount) onCount((res.data.reviews || []).length);
    } catch (err) {
      setError(reportError('chargement des avis', err) || 'Erreur de chargement');
    }
  }, [adminKey, onCount]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const updateReviewStatus = async (reviewId, newStatus) => {
    try {
      await adminPut(`/api/admin/reviews/${reviewId}/status`, adminKey, { new_status: newStatus });
      toast.success(newStatus === 'approuve' ? 'Avis approuvé et publié' : 'Avis rejeté');
      fetchReviews();
    } catch (err) {
      reportError('mise à jour de l\'avis', err, () => toast.error('Erreur lors de la mise à jour'));
    }
  };

  if (error) {
    return (
      <Card className="p-6 text-center text-sm text-destructive" data-testid="reviews-error">
        {error}<div className="mt-3"><Button size="sm" variant="outline" onClick={fetchReviews}>Réessayer</Button></div>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-reviews-tab">
      {reviews.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-muted-foreground">Aucun avis client.</p></Card>
      ) : (
        reviews.map((review) => {
          const meta = reviewStatusMeta(review.status);
          return (
            <motion.div key={review._id} initial="hidden" animate="visible" variants={fadeUp}>
              <Card className="p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <Badge className={`${meta.cls} hover:opacity-90`}>{meta.label}</Badge>
                      <div className="flex items-center gap-0.5" aria-label={`${review.note} sur 5`}>
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= review.note ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <span className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />{formatDate(review.created_at)}</span>
                    </div>
                    <h3 className="font-semibold text-sm">{review.nom}</h3>
                    <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                      <span><Mail className="h-3 w-3 inline mr-1" />{review.email}</span>
                      <span><PhoneIcon className="h-3 w-3 inline mr-1" />{review.telephone}</span>
                    </div>
                    {review.service && <Badge variant="secondary" className="text-xs mt-2">{review.service}</Badge>}
                    {review.titre && <p className="text-sm font-medium mt-2">{review.titre}</p>}
                    <p className="text-sm text-muted-foreground mt-1">« {review.commentaire} »</p>
                    {review.photos?.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {review.photos.map((p, i) => (
                          <div key={p.filename || p.data?.slice(-24) || `photo-${i}`} className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                            <img src={p.data} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {review.status !== 'approuve' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updateReviewStatus(review._id, 'approuve')}>
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approuver
                      </Button>
                    )}
                    {review.status !== 'rejete' && (
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => updateReviewStatus(review._id, 'rejete')}>
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Rejeter
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })
      )}
    </div>
  );
}
